/**
 * SectionRenderer — iterates the catalog filtered by sectionId, applies
 * the engine's visibility filter, collapses adjacent name-component
 * fields into a NameComposite, groups repeatable fields under their
 * parent group renderer, and dispatches each remaining field through
 * FieldRenderer.
 *
 *
 * The grouping pipeline runs in three passes:
 *
 *   1. **Visibility filter.** Drops every field whose
 *      `engine.getFieldStatus(id).visible === false`.
 *   2. **Repeatable-group collapse.** Fields with `repeatableGroupId`
 *      are deferred and rendered together once via a
 *      RepeatableGroupRenderer (one renderer per group). The first
 *      occurrence of a repeatable field in the section's iteration
 *      order anchors the renderer's render position.
 *   3. **Name-composite collapse.** Three text fields whose ids share
 *      a common prefix and end in `.first`, `.middle`, `.last` (in any
 *      order) are collapsed into a single NameComposite render unit.
 *      The first of the three (by catalog order) anchors the unit;
 *      the other two are skipped from the iteration.
 *
 * Anything not collapsed dispatches one field at a time through
 * FieldRenderer.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  type AnswerValue,
  type FieldDefinition,
  type FieldId,
  type ValidationIssue,
} from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';
import {
  AddressComposite,
  AddressSearchInput,
  NameComposite,
  type IAddressFieldEntry,
  type IAddressFooterToggle,
  type IAddressPrefillButton,
  type IGoogleMapsLoader,
  type IUploadedFileMetadata,
  type TAddressParts,
} from '@inside-us-aos/ui';

import { resolveFieldOptions } from './optionsResolver';
import { getAddressClusterKey } from './addressClustering';
import { projectAddressEntriesForCountry } from './addressEntryProjection';

import {
  DOCUMENT_GROUP_METADATA,
  DOCUMENT_GROUP_ORDER,
  getDocumentGroupKey,
  type TDocumentGroupKey,
} from './documentGroups';
import { ComputedTotalTile } from './ComputedTotalTile';
import { useComputedTotalSync } from './computedTotals';
import { EligibilityGroupedRenderer } from './EligibilityGroupedRenderer';
import { FieldRenderer } from './FieldRenderer';
import { HouseholdIncomeMeterTile } from './HouseholdIncomeMeterTile';
import { IncomeThresholdSuccessTile } from './IncomeThresholdTile';
import { useIncomeThreshold } from './incomeThreshold';
import { PrefillBanner } from './PrefillBanner';
import { RepeatableGroupRenderer } from './RepeatableGroupRenderer';
import { SponsorEmploymentPrefillButton } from './sponsorEmploymentPrefill';
import { SubmitForReviewPage } from './SubmitForReviewPage';
import {
  getFieldsForSection,
  getSectionDisplayLabel,
  getSectionIntro,
  getSectionWhy,
} from './sections';
import { withUserHelpText } from './userHelpText';
import styles from './SectionRenderer.module.css';

export interface SectionRendererProps {
  sectionId: string;
  engine: FormEngine;
  /**
   * Set of touched keys. For singleton fields, the key is the
   * `FieldId` itself. For repeatable-row fields, the key is
   * `${fieldId}#${instanceId}`.
   */
  touchedFieldIds: ReadonlySet<string>;
  onTouchKey: (key: string) => void;
  /**
   * Repeatable group instance ids, keyed by group id. The App owns
   * this state in Phase 7a; Phase 7e moves it to a Redux slice.
   */
  repeatableInstancesByGroup: ReadonlyMap<string, readonly string[]>;
  onAddInstance: (groupId: string) => void;
  onRemoveInstance: (groupId: string, instanceId: string) => void;
  /**
   * Upload bridge (App.tsx → SectionRenderer → FieldRenderer →
   * DocumentUpload). When supplied, document_upload fields call this
   * on file selection.
   */
  uploadDocument?: (
    fieldId: FieldId,
    file: IUploadedFileMetadata,
  ) => Promise<{ uploadedFileId: string }>;
  onUploadError?: (fieldId: string, message: string) => void;
  /**
   * Pre-fill banner wiring. When supplied, the
   * SectionRenderer detects the "all required answered, untouched, not
   * yet completed" state and renders a "Confirm and continue / Review
   * answers" banner instead of the field list. The handler runs
   * `markSectionComplete(sectionId)` followed by the App's `goNext()`.
   *
   * The two pieces of state below are owned by the App; SectionRenderer
   * is purely a consumer. When `onConfirmAndContinue` is omitted, the
   * banner branch is skipped entirely (so legacy callers that haven't
   * adopted Slice 6 see no behavior change).
   */
  onConfirmAndContinue?: (sectionId: string) => void;
  /** Whether this section is already in `completedSections`. */
  isSectionComplete?: boolean;
  /**
   * Phase 8 — when `sectionId === 'signatures'`, the SectionRenderer
   * mounts a SubmitForReviewPage instead of iterating the catalog's
   * signature fields. This prop carries the wiring for that surface;
   * when omitted, the signatures branch falls back to a passive
   * banner-style page (no submit button) so legacy callers (the
   * existing test harness) don't crash.
   */
  submitForReview?: {
    applicationId: string;
    onSubmit: () => Promise<void>;
  };
}

interface NameTriplet {
  prefix: string;
  first: FieldDefinition;
  middle: FieldDefinition;
  last: FieldDefinition;
}

interface AddressCluster {
  prefix: string;
  bagName: string;
  fields: readonly FieldDefinition[];
}

/**
 * Detects three fields in `visibleFields` that share a common prefix
 * (everything before the final `.first` / `.middle` / `.last` segment)
 * and returns them as a NameTriplet. Returns null when the prefix
 * doesn't have all three components or when any of them is part of a
 * repeatable group (the group renderer handles its own dispatch).
 */
function findNameTriplet(
  prefix: string,
  byId: ReadonlyMap<string, FieldDefinition>,
): NameTriplet | null {
  const first = byId.get(`${prefix}.first`);
  const middle = byId.get(`${prefix}.middle`);
  const last = byId.get(`${prefix}.last`);
  if (
    first === undefined ||
    middle === undefined ||
    last === undefined ||
    first.repeatableGroupId !== undefined ||
    middle.repeatableGroupId !== undefined ||
    last.repeatableGroupId !== undefined ||
    first.valueType !== 'text' ||
    middle.valueType !== 'text' ||
    last.valueType !== 'text'
  ) {
    return null;
  }
  return { prefix, first, middle, last };
}

/**
 * Project a NameComposite triplet's labels.
 *
 * Catalog labels follow the pattern `<prefix> first name`,
 * `<prefix> middle name`, `<prefix> last name` (e.g.,
 * `"Parent 1 legal first name"`, `"Sponsor legal first name"`,
 * `"Legal first name"`). Without projection the rendered UI shows:
 *
 *   Parent 1 legal              <- legend (auto-derived prefix)
 *   [Parent 1 legal first name *]
 *   [Parent 1 legal middle name]
 *   [Parent 1 legal last name *]
 *
 * The prefix is repeated four times; the user reads "Parent 1 legal"
 * over and over. Slice 39 strips the prefix from each child input
 * and uses it (suffixed with " name") as the legend:
 *
 *   Parent 1 legal name         <- one heading
 *   [First name *]
 *   [Middle name]
 *   [Last name *]
 *
 * Only fires when all three labels match the standard pattern AND
 * share the same prefix. Non-standard formats (e.g.,
 * `"Principal applicant given name (first name)"`) fall through to
 * the existing `replace()` legend logic with the original child
 * labels intact, so no regression for catalog rows that don't use
 * the standard suffix.
 */
interface IProjectedNameTriplet {
  legend: string;
  firstLabel: string;
  middleLabel: string;
  lastLabel: string;
}

/**
 * Strip catalog parenthetical disambiguators like "(first)" / "(last)"
 * / "(given)" / "(family)" from the tail. The catalog attaches these
 * to a few first/last rows ("given name (first)") but never to the
 * matching middle row, which would otherwise cause the tail-equality
 * check to bail and the projection to no-op. Multi-word parentheticals
 * — "(first name)", "(if different from legal name)" — stay so
 * genuine disclaimers ride into the legend.
 *
 * Preserves the leading whitespace separator so `buildNameLegend` can
 * concatenate `${base}${tail}` and still get a natural " " between
 * "Parent 1 name" and "at birth (if different…)". A tail that strips
 * down to whitespace-only normalizes to "" so the tail-equality check
 * across first / middle / last collapses cleanly.
 */
function stripNameDisambiguator(tail: string): string {
  const stripped = tail.replace(
    /\s*\((first|last|given|family)\)\s*$/i,
    '',
  );
  return /^\s*$/.test(stripped) ? '' : stripped;
}

/**
 * Build the fieldset legend from the catalog prefix + optional tail.
 * Trim trailing dash / em-dash / colon punctuation off the prefix
 * (catalog rows like "Name on Form I-94 — " carry one). When the
 * prefix already contains the word "name" (e.g., "Name on Form I-94"),
 * skip the appended " name" so we don't read as "Name on Form I-94 name".
 */
function buildNameLegend(prefix: string, tail: string): string {
  const trimmed = prefix.replace(/[\s—\-–:]+$/u, '').trim();
  if (trimmed.length === 0) {
    return tail.length > 0 ? `Legal name ${tail}`.trim() : 'Legal name';
  }
  const alreadyHasName = /\bname\b/i.test(trimmed);
  const base = alreadyHasName ? trimmed : `${trimmed} name`;
  return tail.length > 0 ? `${base}${tail}` : base;
}

export function projectNameTriplet(
  firstLabel: string,
  middleLabel: string,
  lastLabel: string,
): IProjectedNameTriplet | null {
  // The catalog uses two naming conventions interchangeably:
  //   - "first / middle / last name" — beneficiary, sponsor, parent, etc.
  //   - "given / middle / family name" — current/prior spouse, child,
  //     petitioner-spouse rows (USCIS legalese on those forms).
  // Both project to the same friendly UI labels — applicants
  // understand "first / middle / last" more reliably.
  //
  // Each suffix regex captures an OPTIONAL trailing qualifier (the
  // ` at birth (if different from legal name)` pattern that the catalog
  // attaches to a few birth-name fields). The qualifier rides along
  // into the fieldset legend so the disclaimer appears once at the
  // top of the group instead of three times — once per child label.
  // Non-capturing groups on the alternation so the optional trailing
  // qualifier sits at index 1 across all three regexes (the previous
  // shape put it at index 2 for first/last and index 1 for middle,
  // so middleMatch[2] was always undefined and the qualifier-aware
  // projection silently bailed).
  const FIRST_SUFFIX = /\s+(?:first|given)\s+name(\s.*)?$/i;
  const MIDDLE_SUFFIX = /\s+middle\s+name(\s.*)?$/i;
  const LAST_SUFFIX = /\s+(?:last|family)\s+name(\s.*)?$/i;

  const firstMatch = firstLabel.match(FIRST_SUFFIX);
  const middleMatch = middleLabel.match(MIDDLE_SUFFIX);
  const lastMatch = lastLabel.match(LAST_SUFFIX);
  if (firstMatch === null || middleMatch === null || lastMatch === null) {
    return null;
  }
  const firstPrefix = firstLabel.slice(0, firstMatch.index ?? 0).trim();
  const middlePrefix = middleLabel.slice(0, middleMatch.index ?? 0).trim();
  const lastPrefix = lastLabel.slice(0, lastMatch.index ?? 0).trim();
  if (firstPrefix !== middlePrefix || firstPrefix !== lastPrefix) {
    return null;
  }
  // Qualifier (e.g., " at birth (if different from legal name)") must
  // be identical across all three sub-fields once redundant
  // "(first)" / "(last)" disambiguators are normalized away.
  const firstTail = stripNameDisambiguator(firstMatch[1] ?? '');
  const middleTail = stripNameDisambiguator(middleMatch[1] ?? '');
  const lastTail = stripNameDisambiguator(lastMatch[1] ?? '');
  if (firstTail !== middleTail || firstTail !== lastTail) {
    return null;
  }
  return {
    legend: buildNameLegend(firstPrefix, firstTail),
    firstLabel: 'First name',
    middleLabel: 'Middle name',
    lastLabel: 'Last name',
  };
}

/**
 * Bumps a counter on each engine answer change so the section
 * re-renders. Mirrors FieldRenderer's local subscription so the
 * SectionRenderer reflects engine writes immediately.
 */
function useEngineRevision(engine: FormEngine): number {
  const [revision, setRevision] = useState(0);
  useEffect(
    () => engine.subscribe(() => setRevision(prev => prev + 1)),
    [engine],
  );
  return revision;
}

function readText(engine: FormEngine, fieldId: FieldId): string {
  const v = engine.getAnswer(fieldId);
  return v && v.type === 'text' ? v.value : '';
}

/**
 * Reads either a `text` or `select` answer as a string. Used by the
 * AddressComposite branch where the cluster mixes text inputs (street,
 * city, ZIP) with select inputs (country, state, unit_type). A pure
 * `readText` reader returns `''` for a `select`-typed answer, which
 * silently wipes the dropdown's displayed value on every re-render
 * after the user makes a selection — observed as country/state/unit
 * selections "not persisting."
 */
function readTextOrSelect(engine: FormEngine, fieldId: FieldId): string {
  const v = engine.getAnswer(fieldId);
  if (!v) return '';
  if (v.type === 'text' || v.type === 'select') return v.value;
  return '';
}

/**
 * Address-bag → catalog-suffix lookup for the AddressSearchInput.
 *
 * The cluster carries fields whose ids end in
 * {street_number_and_name, in_care_of_name, unit_type, unit_number,
 * city, state, zip_code, country, province, postal_code, region}.
 * We project the engine's current answers into the 11-key
 * {@link TAddressParts} shape the search component renders summaries
 * from. Suffixes the cluster doesn't include get an empty string.
 */
function readAddressPartsFromCluster(
  engine: FormEngine,
  fields: readonly FieldDefinition[],
): TAddressParts {
  const parts: TAddressParts = {
    street_number_and_name: '',
    in_care_of_name: '',
    unit_type: '',
    unit_number: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    province: '',
    postal_code: '',
    region: '',
  };
  for (const field of fields) {
    const fieldId = field.id as string;
    const last = fieldId.split('.').pop() ?? '';
    if (last in parts) {
      parts[last as keyof TAddressParts] = readTextOrSelect(engine, field.id);
    }
  }
  return parts;
}

/**
 * Write the decomposed Place parts back to the engine, one
 * setAnswer per cluster field whose suffix is in `parts`. Touches
 * each written field so the engine surfaces validation issues
 * immediately if the picked Place is missing something USCIS
 * requires.
 */
function writeAddressPartsToCluster(
  engine: FormEngine,
  fields: readonly FieldDefinition[],
  parts: Partial<TAddressParts>,
  onTouchKey: (key: string) => void,
): void {
  for (const field of fields) {
    const fieldId = field.id as string;
    const last = fieldId.split('.').pop() ?? '';
    if (!(last in parts)) continue;
    const next = parts[last as keyof TAddressParts] ?? '';
    if (field.valueType === 'select') {
      engine.setAnswer(field.id, { type: 'select', value: next });
    } else {
      engine.setAnswer(field.id, { type: 'text', value: next });
    }
    onTouchKey(field.id);
  }
}

/**
 * Lazily-instantiated Google Maps SDK loader. Returns `undefined`
 * when `VITE_GOOGLE_MAPS_API_KEY` isn't set (developer hasn't
 * provisioned a key yet, OR we're under Vitest where the env doesn't
 * include it). The AddressSearchInput renders the manual 6-field
 * grid as a fallback in that case — zero UX cliff.
 *
 * We do NOT bundle `@googlemaps/js-api-loader` at this stage; the
 * production wiring is deferred until the API key is provisioned.
 * The current loader returns a never-resolving promise as a
 * placeholder: when an API key is set, the input mounts and the user
 * types into it (no autocomplete suggestions yet — the user can
 * always click "Don't see your address?" to reach manual entry).
 *
 * Wiring a real `@googlemaps/js-api-loader` Loader is a follow-up
 * slice; the contract of `IGoogleMapsLoader` is the seam.
 */
const GOOGLE_MAPS_API_KEY: string | undefined =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ??
  undefined;

const GOOGLE_MAPS_LOADER: IGoogleMapsLoader | undefined =
  GOOGLE_MAPS_API_KEY === undefined || GOOGLE_MAPS_API_KEY === ''
    ? undefined
    : { load: () => new Promise(() => {}) };

/**
 * Sponsor sections that don't carry
 * the `sponsor_` prefix in their section ID. Currently just
 * `financials` (the I-864 affidavit-of-support section); kept as a
 * Set so future sponsor sections without the prefix can be added in
 * one place. Used by the party filter in `SectionRenderer` to allow
 * `sponsor.*` fields through these sections.
 */
export const SPONSOR_ONLY_SECTION_IDS: ReadonlySet<string> = new Set([
  'financials',
  // Petition_type carries the sponsor's prior-
  // I-130 disclosure (`sponsor.prior_i130_filings.has_filed_prior`)
  // alongside untargeted application.* petition fields. The petitioner
  // IS the sponsor on a marriage-based AOS, so this section is sponsor-
  // led. Without this entry the sponsor.* required field was orphaned
  // (rendered nowhere, permanently sat in progress.missingFieldIds).
  'petition_type',
]);

/**
 * Sections that intentionally render BOTH
 * party roles (e.g., the unified Documents section uploads the
 * beneficiary's birth certificate AND the sponsor's tax return).
 * The party filter is bypassed entirely for these sections.
 */
export const MULTI_PARTY_SECTION_IDS: ReadonlySet<string> = new Set([
  'documents',
]);

/**
 * Friendly labels for repeatable groups.
 *
 * The previous code used `${groupId} entries` as the fieldset legend,
 * which rendered raw catalog IDs to the user
 * ("beneficiary.other_alien_registration_numbers entries"). This map
 * provides clean human labels per known group; unknown groups fall
 * through to a derived label (last segment, underscores → spaces,
 * sentence-cased).
 */
const REPEATABLE_GROUP_LABELS: ReadonlyMap<string, string> = new Map([
  ['application.additional_info', 'Additional information entries'],
  ['beneficiary.additional_birth_dates', 'Other birth dates on record'],
  ['beneficiary.address_history', 'Past addresses (last 5 years)'],
  ['beneficiary.children', 'Children'],
  ['beneficiary.employment_history', 'Past employment (last 5 years)'],
  ['beneficiary.other_alien_registration_numbers', 'Other A-Numbers'],
  ['beneficiary.other_names', 'Other names you have used'],
  ['beneficiary.prior_marriages', 'Prior marriages'],
  ['document.children.per_child', 'Documents per child'],
  ['document.marriage.beneficiary_prior_terminations', 'Prior-marriage termination evidence (yours)'],
  ['document.marriage.sponsor_prior_terminations', 'Prior-marriage termination evidence (sponsor)'],
  ['document.screening.conviction_dispositions', 'Conviction dispositions'],
  ['document.screening.criminal_dispositions', 'Criminal dispositions'],
  ['document.sponsor_financial.joint_sponsor_packet', 'Joint sponsor packet'],
  ['eligibility.long_term_institutionalization', 'Long-term institutional stays'],
  ['eligibility.organization_memberships', 'Organization memberships'],
  ['eligibility.public_benefits_received', 'Public benefits received'],
  ['sponsor.address_history', "Sponsor's past addresses"],
  ['sponsor.employment_history', "Sponsor's past employment"],
  ['sponsor.household_members_income', 'Household members with income'],
  ['sponsor.other_names', 'Other names the sponsor has used'],
  ['sponsor.prior_marriages', "Sponsor's prior marriages"],
  ['sponsor.tax_history', 'Tax history (last 3 years)'],
]);

/**
 * Sub-headings interleaved into the `financials` section so the
 * mix of employment / income / tax / asset chunks reads as discrete
 * blocks instead of one continuous wall. Each key is the catalog
 * field id of the FIRST field in the block (the "anchor"); when the
 * renderer reaches that anchor, it emits `<h3>` + optional intro
 * before the unit.
 *
 * Adding a new heading is a one-line insertion. The map is sparse —
 * blocks without an entry just continue inline. Headings are
 * suppressed when the anchor is hidden (visibility rule), because
 * `units` is built from `visibleFields`.
 */
export interface IFinancialSubHeading {
  heading: string;
  intro?: string;
}
export const FINANCIAL_SUB_HEADINGS: ReadonlyMap<string, IFinancialSubHeading> =
  new Map([
    [
      'sponsor.current_employment.status',
      { heading: 'Your current employment' },
    ],
    [
      'sponsor.income.individual_annual',
      {
        heading: 'Annual income',
        intro:
          'USCIS compares your household income against 125% of the federal poverty guideline for your household size.',
      },
    ],
    [
      'sponsor.income.filed_tax_returns_three_years',
      {
        heading: 'Tax history',
        intro:
          "Already have an IRS transcript? Upload it on the Documents tab — we'll match the numbers below.",
      },
    ],
    [
      'sponsor.assets.cash_and_accounts_balance',
      {
        heading: 'Assets (optional)',
        intro:
          "Only needed if income alone doesn't meet the threshold. USCIS counts 5× the asset gap (3× for spouses of U.S. citizens, 1× for orphans) toward closing it.",
      },
    ],
    [
      'sponsor.assets.household_members_total',
      { heading: "Household members' assets" },
    ],
    [
      'sponsor.assets.intending_immigrant_cash_and_accounts_balance',
      { heading: "Beneficiary's assets" },
    ],
  ]);

/**
 * Anchor field-id of a render unit — the first FieldDefinition in
 * the unit. Used to look up sub-heading metadata.
 */
function getUnitAnchorFieldId(unit: RenderUnit): string | undefined {
  switch (unit.kind) {
    case 'field':
      return unit.field.id;
    case 'group':
      return unit.fields[0]?.id;
    case 'address':
      return unit.cluster.fields[0]?.id;
    case 'name':
      return unit.triplet.first.id;
    case 'height':
      return unit.feet.id;
  }
}

export function getRepeatableGroupLabel(groupId: string): string {
  const curated = REPEATABLE_GROUP_LABELS.get(groupId);
  if (curated !== undefined) return curated;
  // Fall through: derive from last segment + sentence case.
  const segments = groupId.split('.');
  const last = segments[segments.length - 1] ?? groupId;
  const spaced = last.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface NameRenderUnit {
  kind: 'name';
  triplet: NameTriplet;
}
interface FieldRenderUnit {
  kind: 'field';
  field: FieldDefinition;
}
interface GroupRenderUnit {
  kind: 'group';
  groupId: string;
  fields: readonly FieldDefinition[];
}
interface AddressRenderUnit {
  kind: 'address';
  cluster: AddressCluster;
}
/**
 * Height pair render unit.
 * `<prefix>.height_feet` + `<prefix>.height_inches` render side-by-
 * side under one "Height" legend instead of stacking as two
 * full-line NumberInputs. The catalog stores them as separate
 * fields (USCIS form rows them as ft + in cells); the composite is
 * presentation-only.
 */
interface HeightRenderUnit {
  kind: 'height';
  feet: FieldDefinition;
  inches: FieldDefinition;
}
type RenderUnit =
  | NameRenderUnit
  | FieldRenderUnit
  | GroupRenderUnit
  | AddressRenderUnit
  | HeightRenderUnit;

/**
 * Plans the section's render units from a visible-field list. Pure
 * function; tested independently.
 */
export function planSectionRenderUnits(
  visibleFields: readonly FieldDefinition[],
): readonly RenderUnit[] {
  const byId = new Map<string, FieldDefinition>(
    visibleFields.map(f => [f.id, f]),
  );
  const consumed = new Set<string>();
  const groupsAnchored = new Set<string>();
  const addressAnchored = new Set<string>();
  const allFieldsInSection = visibleFields;
  const units: RenderUnit[] = [];

  for (const field of allFieldsInSection) {
    if (consumed.has(field.id)) continue;

    // Repeatable groups: first occurrence in section order anchors
    // the group's render position. Subsequent fields with the same
    // groupId are skipped. Address clustering does NOT apply inside
    // repeatable rows in Phase 7a — the group renderer mounts the
    // raw FieldRenderer for each row's fields.
    if (field.repeatableGroupId !== undefined) {
      const groupId = field.repeatableGroupId;
      if (groupsAnchored.has(groupId)) {
        consumed.add(field.id);
        continue;
      }
      groupsAnchored.add(groupId);
      const groupFields = allFieldsInSection.filter(
        f => f.repeatableGroupId === groupId,
      );
      for (const gf of groupFields) consumed.add(gf.id);
      units.push({ kind: 'group', groupId, fields: groupFields });
      continue;
    }

    // Address cluster: collapse all section fields sharing the
    // address-bag prefix. We require ≥2 sub-fields to make the
    // composite worthwhile; a lone street field stays standalone.
    const clusterKey = getAddressClusterKey(field);
    if (clusterKey !== null && !addressAnchored.has(clusterKey)) {
      const clusterFields = allFieldsInSection.filter(
        f =>
          f.repeatableGroupId === undefined &&
          getAddressClusterKey(f) === clusterKey,
      );
      if (clusterFields.length >= 2) {
        // 2026-05-24 — explicit render order matching the user's
        // stated design: street → unit-type → conditional unit-number
        // → in-care-of → city → state/province → zip/postal → country.
        //
        // Pre-2026-05-24 the sort put country FIRST so the picker
        // could gate state/ZIP vs province/postal visibility. That
        // worked but read backwards: applicants kept asking "why am
        // I picking country before street?" Default country to US
        // and put it last; non-US applicants override it.
        const ADDRESS_DISPLAY_ORDER: Record<string, number> = {
          in_care_of_name: 0,
          street_number_and_name: 1,
          unit_type: 2,
          unit_number: 3,
          city: 4,
          state: 5,
          province: 5,
          zip_code: 6,
          postal_code: 6,
          country: 7,
          // 2026-05-26 — move-in / move-out dates appear last so the
          // address bag reads top-down: who/where → when.
          date_from: 8,
          date_to: 9,
        };
        const rank = (id: string): number => {
          const suffix = id.split('.').pop() ?? '';
          return ADDRESS_DISPLAY_ORDER[suffix] ?? 99;
        };
        const orderedClusterFields = [...clusterFields].sort((a, b) => {
          const aRank = rank(a.id as string);
          const bRank = rank(b.id as string);
          if (aRank !== bRank) return aRank - bRank;
          return 0; // stable sort preserves catalog order on ties
        });
        addressAnchored.add(clusterKey);
        for (const cf of clusterFields) consumed.add(cf.id);
        const segments = clusterKey.split('.');
        const bagName = segments[segments.length - 1];
        units.push({
          kind: 'address',
          cluster: {
            prefix: clusterKey,
            bagName,
            fields: orderedClusterFields,
          },
        });
        continue;
      }
    }

    // Name composite: when the field id ends in `.first`, look for
    // matching sibling `.middle` / `.last` in the same section.
    //
    // Extended to ALSO check `.last` and
    // `.middle` so the triplet detection isn't catalog-order
    // dependent. Reproduction: `beneficiary.current_spouse.name.
    // legal.last` is declared BEFORE `.first` in the catalog.
    // The original `.first`-only check meant `.last` fell through
    // to standalone-field emission first, then `.first` emitted
    // the NameComposite — producing two render units, two inputs
    // for the same field.
    const idStr = field.id as string;
    let nameTripletPrefix: string | null = null;
    if (field.valueType === 'text') {
      if (idStr.endsWith('.first')) {
        nameTripletPrefix = idStr.slice(0, -'.first'.length);
      } else if (idStr.endsWith('.middle')) {
        nameTripletPrefix = idStr.slice(0, -'.middle'.length);
      } else if (idStr.endsWith('.last')) {
        nameTripletPrefix = idStr.slice(0, -'.last'.length);
      }
    }
    if (nameTripletPrefix !== null) {
      const triplet = findNameTriplet(nameTripletPrefix, byId);
      if (triplet !== null) {
        consumed.add(triplet.first.id);
        consumed.add(triplet.middle.id);
        consumed.add(triplet.last.id);
        units.push({ kind: 'name', triplet });
        continue;
      }
    }

    // Height pair: `.height_feet` +
    // `.height_inches` render side-by-side under one "Height" legend
    // instead of stacking as two separate NumberInput rows.
    if (field.id.endsWith('.height_feet')) {
      const inchesId = field.id.slice(0, -'.height_feet'.length) + '.height_inches';
      const inches = byId.get(inchesId);
      if (inches !== undefined) {
        consumed.add(field.id);
        consumed.add(inches.id);
        units.push({ kind: 'height', feet: field, inches });
        continue;
      }
    }

    consumed.add(field.id);
    units.push({ kind: 'field', field });
  }

  return units;
}

export function SectionRenderer(props: SectionRendererProps): JSX.Element {
  const {
    sectionId,
    engine,
    touchedFieldIds,
    onTouchKey,
    repeatableInstancesByGroup,
    onAddInstance,
    onRemoveInstance,
    uploadDocument,
    onUploadError,
    onConfirmAndContinue,
    isSectionComplete = false,
    submitForReview,
  } = props;

  // Subscribe to engine revisions so visibility re-evaluates on every
  // answer change. The revision counter doubles as a memoization key
  // for the derived field lists below — we recompute exactly when the
  // engine's answers (and therefore its visibility judgments) have
  // changed, not on unrelated re-renders (e.g., parent state churn).
  const engineRevision = useEngineRevision(engine);

  // Phase 7i — replace `aosV1FieldDefinitions.filter(f => f.sectionId
  // === sectionId)` (an O(N=580) scan, was running on every render)
  // with the precomputed sectionId index from sections.ts. Lookup is
  // O(1) and the underlying array is referentially stable.
  //
  // Party filter, fixed in slice 30.
  //
  // Some catalog sections (notably `biographic`) carry fields for
  // both beneficiary AND sponsor (e.g., `beneficiary.biographic.
  // height_feet` AND `sponsor.biographic.height_feet`), so without
  // filtering the user sees every biographic question rendered
  // twice. The original Slice 29 filter ("sections starting with
  // `sponsor_` show sponsor fields, everything else shows
  // beneficiary fields") accidentally broke the `financials`
  // section, which is sponsor-only but doesn't have the
  // `sponsor_` prefix in its ID. Fixed by explicitly enumerating
  // sponsor sections.
  //
  // Other prefixes (`application.*`, `eligibility.*`, `document.*`,
  // `interpreter.*`, `preparer.*`) pass through to whichever section
  // the catalog assigns them to.
  const sectionFields = useMemo(() => {
    const all = getFieldsForSection(sectionId);
    const isMultiParty = MULTI_PARTY_SECTION_IDS.has(sectionId);
    const isSponsorSection =
      sectionId.startsWith('sponsor_') || SPONSOR_ONLY_SECTION_IDS.has(sectionId);
    return all.filter(f => {
      const fid = f.id as string;
      if (isMultiParty) return true; // Slice 39 — render every party.
      if (fid.startsWith('beneficiary.')) return !isSponsorSection;
      if (fid.startsWith('sponsor.')) return isSponsorSection;
      // application.*, eligibility.*, document.*, interpreter.*,
      // preparer.* — pass through to whichever section the catalog
      // assigns.
      return true;
    });
  }, [sectionId]);

  // Live evaluation of the I-864 income threshold. When the sponsor's
  // income alone clears 125% × FPG for their household size, Part 7
  // (assets) and the household co-filer block are not required by
  // the form — we hide them and surface a single success tile in
  // their place. See `incomeThreshold.ts` for the math.
  const incomeThreshold = useIncomeThreshold(engine);
  const autoSkipAssetsAndCoFiler =
    sectionId === 'financials' && incomeThreshold.meets;

  // When the threshold is met, force-answer the co-filer gate to
  // `false` in the engine so the cascading visibility on the
  // co-filer fields (the repeatable group, the I-864A coordination
  // booleans, etc.) resolves correctly AND so the engine doesn't
  // flag the required gate question as missing in section progress.
  // Idempotent: only writes when the current value isn't already
  // false. Triggers on `meets` flip — does NOT loop because the
  // equality check short-circuits the second pass.
  useEffect(() => {
    if (!autoSkipAssetsAndCoFiler) return;
    const coFilerId = 'sponsor.income.has_household_co_filer' as FieldId;
    const current = engine.getAnswer(coFilerId);
    if (current?.type === 'boolean' && current.value === false) return;
    engine.setAnswer(coFilerId, { type: 'boolean', value: false });
  }, [autoSkipAssetsAndCoFiler, engine]);

  // Visibility filter still depends on engine state, so memoize keyed
  // on the engine revision. Same for the render-unit plan (pure
  // function of visibleFields). Before this memo, both ran on every
  // render, even when the answer that triggered the render was in a
  // different section.
  const visibleFields = useMemo(
    () => {
      const base = sectionFields.filter(
        f => engine.getFieldStatus(f.id).visible,
      );
      if (!autoSkipAssetsAndCoFiler) return base;
      // Income clears threshold — hide the Part 7 asset block AND
      // the co-filer Yes/No gate. The downstream co-filer fields are
      // already hidden via their `visibility: whenSponsorHasHouseholdCoFiler`
      // rule (since we forced the gate to false above).
      return base.filter(f => {
        const fid = f.id as string;
        if (fid === 'sponsor.income.has_household_co_filer') return false;
        if (fid.startsWith('sponsor.assets.')) return false;
        return true;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- engineRevision is the cache key
    [sectionFields, engine, engineRevision, autoSkipAssetsAndCoFiler],
  );
  const units = useMemo(
    () => planSectionRenderUnits(visibleFields),
    [visibleFields],
  );

  // Keep `field.computed` totals in sync with their addends. The hook
  // subscribes to engine revisions and writes the recomputed sum back
  // to the engine on every change — so the displayed tile, persisted
  // snapshot, and PDF emission all see the same number without asking
  // the applicant to type it manually.
  const computedFields = useMemo(
    () => visibleFields.filter(f => f.computed !== undefined),
    [visibleFields],
  );
  useComputedTotalSync(engine, computedFields);

  // Phase 7f — documents section is the only one that sub-groups its
  // render units under per-group headings (the section's 30-ish file
  // uploads are otherwise a single undifferentiated list). We bucket
  // the already-planned units by their `document.<group>.<field>` key,
  // taking the first field of each unit as the bucket anchor (groups
  // and address clusters never mix document-group keys).
  const docGroupedUnits = (() => {
    if (sectionId !== 'documents') return null;
    const buckets = new Map<TDocumentGroupKey, RenderUnit[]>();
    for (const unit of units) {
      const anchor: FieldDefinition | undefined =
        unit.kind === 'field'
          ? unit.field
          : unit.kind === 'name'
          ? unit.triplet.first
          : unit.kind === 'address'
          ? unit.cluster.fields[0]
          : unit.kind === 'height'
          ? unit.feet
          : unit.fields[0];
      if (anchor === undefined) continue;
      const key = getDocumentGroupKey(anchor.id as string);
      if (key === null) continue;
      const existing = buckets.get(key);
      if (existing === undefined) {
        buckets.set(key, [unit]);
      } else {
        existing.push(unit);
      }
    }
    return buckets;
  })();

  const validation = engine.getValidationState();
  const issuesByFieldId = validation.issuesByFieldId;

  // Phase 7j Slice A — promote the catalog's
  // `<party>.mailing_address.same_as_current_physical` boolean from a
  // standalone Yes/No radio to a checkbox attached to the always-visible
  // address card in the section. The two address bags involved
  // (`<party>.current_physical_address` and `<party>.mailing_address`)
  // share the same `<party>` prefix; one of them is gated on this
  // boolean and is therefore conditionally visible, while the other is
  // unconditional. Only the unconditional cluster appears in the
  // `units` array at default state, and that's the one we attach the
  // checkbox to.
  //
  // Display semantics are inverted from the catalog: the catalog stores
  // `true = mailing same as physical` (default state collapses the
  // mailing block); the checkbox reads "Mail goes to a different
  // address" with `checked = true` writing `boolean false` (mailing
  // differs) and `checked = false` writing `boolean true` (same).
  const mailingToggleByClusterPrefix = new Map<string, FieldDefinition>();
  const fieldIdsAbsorbedByCluster = new Set<string>();
  // Track per-boolean attachment so the toggle attaches to at most ONE
  // address card per party. When the user checks the box and the
  // previously gated cluster (e.g., sponsor.current_physical_address)
  // becomes visible, we don't want to render a second checkbox there.
  const attachedBooleanIds = new Set<string>();
  for (const unit of units) {
    if (unit.kind !== 'address') continue;
    const clusterPrefix = unit.cluster.prefix;
    const bagSuffixes = ['.current_physical_address', '.mailing_address'];
    const matched = bagSuffixes.find(s => clusterPrefix.endsWith(s));
    if (matched === undefined) continue;
    const partyPrefix = clusterPrefix.slice(0, -matched.length);
    const mailingSameAsId =
      `${partyPrefix}.mailing_address.same_as_current_physical`;
    if (attachedBooleanIds.has(mailingSameAsId)) continue;
    const sibling = sectionFields.find(f => f.id === mailingSameAsId);
    if (sibling === undefined || sibling.valueType !== 'boolean') continue;
    mailingToggleByClusterPrefix.set(clusterPrefix, sibling);
    fieldIdsAbsorbedByCluster.add(sibling.id);
    attachedBooleanIds.add(mailingSameAsId);
  }

  // Phase 7j Slice B — sponsor's current physical cluster gets an
  // optional "Use beneficiary's address" prefill button. Hidden when
  // the beneficiary side is fully empty (nothing to copy).
  const beneficiaryPhysicalSubFields: Record<string, FieldId | undefined> = {
    street_number_and_name: undefined,
    unit_type: undefined,
    unit_number: undefined,
    city: undefined,
    state: undefined,
    zip_code: undefined,
    country: undefined,
    province: undefined,
    postal_code: undefined,
  };
  // Build the lookup once per render — small constant cost.
  const SUB_FIELD_NAMES = Object.keys(beneficiaryPhysicalSubFields);
  for (const name of SUB_FIELD_NAMES) {
    beneficiaryPhysicalSubFields[name] =
      `beneficiary.current_physical_address.${name}` as FieldId;
  }

  function readFieldString(fieldId: FieldId): string {
    return readTextOrSelect(engine, fieldId);
  }

  function beneficiaryPhysicalHasAnyValue(): boolean {
    for (const name of SUB_FIELD_NAMES) {
      const id = beneficiaryPhysicalSubFields[name];
      if (id === undefined) continue;
      if (readFieldString(id).trim().length > 0) return true;
    }
    return false;
  }

  function copyBeneficiaryPhysicalToSponsor(): void {
    for (const name of SUB_FIELD_NAMES) {
      const fromId = beneficiaryPhysicalSubFields[name];
      const toId =
        `sponsor.current_physical_address.${name}` as FieldId;
      if (fromId === undefined) continue;
      const v = engine.getAnswer(fromId);
      if (v === undefined) continue;
      if (v.type === 'text') {
        engine.setAnswer(toId, { type: 'text', value: v.value });
      } else if (v.type === 'select') {
        engine.setAnswer(toId, { type: 'select', value: v.value });
      }
      onTouchKey(toId);
    }
  }

  // Section intro string (sparse map; undefined for
  // sections without an authored intro).
  const intro = getSectionIntro(sectionId);
  // "Why USCIS asks" copy. Sparse map
  // — sections without an authored why simply skip the collapsible.
  const why = getSectionWhy(sectionId);

  // Pre-fill banner detection. The banner shows
  // when ALL of:
  //   1. the section has > 0 required-visible fields,
  //   2. every required-visible field is already answered,
  //   3. the user hasn't touched any field in this section yet,
  //   4. the section isn't already marked complete.
  // `dismissed` is local state — clicking "Review answers" hides the
  // banner for the rest of this component's lifecycle without persisting.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const sectionFieldIdSet = new Set<string>(sectionFields.map(f => f.id));
  const sectionTouched = (() => {
    for (const key of touchedFieldIds) {
      // Singleton keys are bare FieldIds; repeatable keys are
      // `${fieldId}#${instanceId}`. Match either by stripping the
      // `#...` suffix and looking up in the section's id set.
      const hashIdx = key.indexOf('#');
      const baseId = hashIdx >= 0 ? key.slice(0, hashIdx) : key;
      if (sectionFieldIdSet.has(baseId)) return true;
    }
    return false;
  })();
  const sectionProgress = engine.getAllSectionProgress().get(sectionId);
  const showPrefillBanner =
    onConfirmAndContinue !== undefined &&
    !bannerDismissed &&
    !isSectionComplete &&
    !sectionTouched &&
    sectionProgress !== undefined &&
    sectionProgress.required > 0 &&
    sectionProgress.missingFieldIds.length === 0;

  return (
    <section
      aria-label={getSectionDisplayLabel(sectionId)}
      data-section-id={sectionId}
    >
      <h2 className={styles.section__heading}>
        {getSectionDisplayLabel(sectionId)}
      </h2>
      {intro !== undefined ? (
        <p className={styles.section__intro} data-section-intro={sectionId}>
          {intro}
        </p>
      ) : null}
      {why !== undefined ? (
        // Collapsible "Why USCIS asks
        // this." Native <details> element so it works without
        // JavaScript (state is in the DOM, not React) and keyboard
        // navigation / screen-reader semantics come for free. The
        // initial state is closed; users opt in only when they want
        // the context.
        <details
          className={styles.section__why}
          data-section-why={sectionId}
        >
          <summary className={styles.section__why_summary}>
            Why USCIS asks this
          </summary>
          <p className={styles.section__why_body}>{why}</p>
        </details>
      ) : null}
      {showPrefillBanner ? (
        <PrefillBanner
          sectionId={sectionId}
          onConfirmAndContinue={onConfirmAndContinue!}
          onReviewAnswers={() => setBannerDismissed(true)}
        />
      ) : null}
      {sectionId === 'signatures' ? (
        // Phase 8 — the signatures section's render path is repurposed.
        // Instead of iterating the catalog signature fields (which ask
        // the applicant to pre-attest "I have signed in pen" BEFORE the
        // case team has even printed the packet), we mount a clean
        // Submit-for-review surface. The catalog fields stay (the case
        // team or print-time wiring fills them at packet-assembly time);
        // they're just not editable in-app.
        //
        // When `submitForReview` is omitted (legacy harness), fall back
        // to a no-op submit so the surface still renders.
        <SubmitForReviewPage
          engine={engine}
          applicationId={submitForReview?.applicationId ?? sectionId}
          onSubmit={submitForReview?.onSubmit ?? (async () => {})}
        />
      ) : sectionId === 'eligibility' ? (
        // Pattern C: group ~70 yes/no
        // screening questions into 8 conceptual buckets with "None
        // apply" fast paths. Replaces the wall-of-radios that drove
        // applicant fatigue + autopilot risk.
        //
        // Split routing: booleans go through
        // the grouped renderer; non-boolean fields (selects, dates,
        // repeatable groups for organization memberships / long-term
        // institutional stays / public benefits) go through the
        // standard render-unit path so RepeatableGroupRenderer can
        // mount them with proper per-instance state. Routing all
        // non-booleans through `<FieldRenderer>` here previously
        // crashed for repeatable fields (engine.getAnswer throws
        // without a groupInstanceId).
        (() => {
          const booleanFields = visibleFields.filter(
            f => f.valueType === 'boolean',
          );
          const nonBooleanFields = visibleFields.filter(
            f => f.valueType !== 'boolean',
          );
          const nonBooleanUnits = planSectionRenderUnits(nonBooleanFields);
          return (
            <>
              <EligibilityGroupedRenderer
                engine={engine}
                visibleFields={booleanFields}
                touchedFieldIds={touchedFieldIds}
                onTouchKey={onTouchKey}
                issuesByFieldId={issuesByFieldId}
              />
              {nonBooleanUnits.length > 0 ? (
                <div
                  className={styles.eligibility_other}
                  data-eligibility-other
                >
                  <h3 className={styles.eligibility_other__heading}>
                    Additional details
                  </h3>
                  {nonBooleanUnits.map(unit => renderUnit(unit))}
                </div>
              ) : null}
            </>
          );
        })()
      ) : docGroupedUnits !== null
        ? DOCUMENT_GROUP_ORDER.flatMap(key => {
            const groupUnits = docGroupedUnits.get(key);
            if (groupUnits === undefined || groupUnits.length === 0) {
              // Group with no visible fields — skip the heading entirely
              // so applicants who have nothing to upload under e.g.
              // "Background & screening" don't see an empty section.
              return [];
            }
            const meta = DOCUMENT_GROUP_METADATA[key];
            return [
              <div
                key={`docgroup-${key}`}
                className={styles.doc_group}
                data-doc-group={key}
              >
                <h3 className={styles.doc_group__heading}>
                  {meta.displayLabel}
                </h3>
                <p className={styles.doc_group__intro}>{meta.intro}</p>
                {groupUnits.map(unit => renderUnit(unit))}
              </div>,
            ];
          })
        : sectionId === 'financials'
        ? [
            // Auto-skip success tile: when the sponsor's income clears
            // 125% × FPG for their household size, Part 7 (assets) and
            // the household co-filer block are hidden from `visibleFields`
            // above. This tile sits where they used to so users
            // understand the page didn't just lose sections.
            autoSkipAssetsAndCoFiler ? (
              <IncomeThresholdSuccessTile
                key="income-threshold-success"
                state={incomeThreshold}
              />
            ) : null,
            ...units.flatMap(unit => renderFinancialsUnit(unit)),
          ]
        : units.map(unit => renderUnit(unit))}
    </section>
  );

  function renderFinancialsUnit(unit: RenderUnit): (JSX.Element | null)[] {
    // Interleave the per-block sub-headings declared in
    // `FINANCIAL_SUB_HEADINGS`. Anchor = first field id of the unit;
    // emit heading + intro before the unit when matched. Headings
    // are auto-suppressed when the anchor is hidden (visibility
    // rule) because `units` is built from `visibleFields`.
    const anchor = getUnitAnchorFieldId(unit);
    const meta =
      anchor !== undefined ? FINANCIAL_SUB_HEADINGS.get(anchor) : undefined;
    const rendered = renderUnit(unit);
    // The current-employment block gets a "Use my current job from
    // above" button when the sponsor has already filled an employment-
    // history row that's still ongoing. Surfaced alongside the sub-
    // heading so the affordance is obvious.
    const showEmploymentPrefill =
      anchor === 'sponsor.current_employment.status';
    if (meta === undefined && !showEmploymentPrefill) {
      return [rendered];
    }
    return [
      <div
        key={`fin-subhead-${anchor}`}
        className={styles.financial_subhead}
        data-financial-subhead={anchor}
      >
        {meta !== undefined ? (
          <>
            <h3 className={styles.financial_subhead__heading}>
              {meta.heading}
            </h3>
            {meta.intro !== undefined ? (
              <p className={styles.financial_subhead__intro}>{meta.intro}</p>
            ) : null}
          </>
        ) : null}
        {showEmploymentPrefill ? (
          <SponsorEmploymentPrefillButton
            engine={engine}
            onTouchKey={onTouchKey}
          />
        ) : null}
      </div>,
      rendered,
    ];
  }

  function renderUnit(unit: RenderUnit): JSX.Element | null {
    // Phase 7j Slice A — fields whose Yes/No is absorbed into an
    // address cluster footer checkbox should not also render as a
    // standalone radio.
    if (
      unit.kind === 'field' &&
      fieldIdsAbsorbedByCluster.has(unit.field.id)
    ) {
      return null;
    }
    if (unit.kind === 'address') {
      const { cluster } = unit;
      const rawEntries: IAddressFieldEntry[] = cluster.fields.map(field => ({
        // Resolve `required` to a literal boolean so the input
        // primitives can render the `*` asterisk on conditional-
        // required fields (e.g. intended_address.* which is required
        // only when differs_from_current=true). The primitives only
        // show the marker for `field.required === true`, so a
        // RuleExpression silently never renders the marker.
        field: {
          ...withUserHelpText(field),
          required:
            engine.getFieldStatus(field.id).required ||
            field.required === true,
        },
        value: readTextOrSelect(engine, field.id),
        issues: issuesByFieldId.get(field.id) ?? [],
        touched: touchedFieldIds.has(field.id),
        selectOptions:
          field.valueType === 'select'
            ? resolveFieldOptions(field)
            : undefined,
        onChange: (next: string) => {
          // Always write as text (engine schema for the catalog
          // field) — the projection below may swap the DISPLAY type
          // to 'select' for a US-state dropdown, but the stored value
          // is the same string ("MD") either way.
          const valueType = field.valueType;
          if (valueType === 'select') {
            engine.setAnswer(field.id, { type: 'select', value: next });
          } else {
            engine.setAnswer(field.id, { type: 'text', value: next });
          }
          onTouchKey(field.id);
        },
        onBlur: () => onTouchKey(field.id),
      }));
      const entries = projectAddressEntriesForCountry(rawEntries);
      // Friendly legend: title-case the bag name (e.g.,
      // "Mailing Address"). The address-bag prefix (e.g.,
      // "beneficiary.mailing_address") tells us who the address
      // belongs to; we keep the legend short so the section
      // header carries the party context.
      //
      // Special case for `*.<event>.place` clusters
      // (e.g., `beneficiary.birth.place`,
      // `beneficiary.current_spouse.marriage.place`). A bare
      // "Place" legend gives the user no idea what kind of place
      // they're filling — birth, marriage, residence? We promote
      // the parent segment ("birth", "marriage") into the legend:
      // "Place of birth", "Place of marriage". `marriage_place`
      // and similar already-descriptive bag names fall through
      // to the title-case path.
      const legend = (() => {
        if (cluster.bagName === 'place') {
          const segments = cluster.prefix.split('.');
          // Last segment IS "place"; the segment before it names
          // the event the place belongs to.
          const parent = segments[segments.length - 2];
          if (parent !== undefined && parent.length > 0) {
            return `Place of ${parent.replace(/_/g, ' ')}`;
          }
        }
        return cluster.bagName
          .split('_')
          .map(p => (p.length === 0 ? p : p[0].toUpperCase() + p.slice(1)))
          .join(' ');
      })();
      // Build the engine-read parts so the search component can
      // decide search-vs-summary and render the summary card.
      const searchValues: TAddressParts =
        readAddressPartsFromCluster(engine, cluster.fields);

      // Phase 7j Slice A — footer toggle for the
      // mailing-same-as-physical boolean. When this cluster is the
      // current physical address and the section also carries the
      // sibling boolean, mount a checkbox below the field grid.
      const mailingToggleField = mailingToggleByClusterPrefix.get(
        cluster.prefix,
      );
      let footerToggle: IAddressFooterToggle | undefined;
      if (mailingToggleField !== undefined) {
        const v = engine.getAnswer(mailingToggleField.id);
        // Default unchecked == "same as physical = true". A missing
        // answer means the user hasn't decided yet; we default to the
        // common case (same as physical) so the mailing fields stay
        // hidden until the user opts out.
        const sameAsPhysical = v?.type === 'boolean' ? v.value : true;
        footerToggle = {
          id: `address-footer-${cluster.prefix}-mail-different`,
          // Shorter, more declarative
          // copy ("Mailing address is different" reads as a checkbox
          // statement; the previous "Mail goes to a different address"
          // sounded like a future-tense narrative).
          label: 'Mailing address is different',
          checked: !sameAsPhysical,
          onChange: (next: boolean) => {
            engine.setAnswer(mailingToggleField.id, {
              type: 'boolean',
              value: !next,
            });
            onTouchKey(mailingToggleField.id);
          },
        };
      }

      // Phase 7j Slice B — sponsor current physical only: "Use
      // beneficiary's address" header button. Hidden when beneficiary
      // physical address is fully empty.
      let prefillButton: IAddressPrefillButton | undefined;
      if (
        cluster.prefix === 'sponsor.current_physical_address' &&
        beneficiaryPhysicalHasAnyValue()
      ) {
        prefillButton = {
          label: "Use beneficiary's address",
          onClick: copyBeneficiaryPhysicalToSponsor,
        };
      }

      return (
        <AddressSearchInput
          key={cluster.prefix}
          legend={legend}
          values={searchValues}
          loader={GOOGLE_MAPS_LOADER}
          onAddressPicked={parts =>
            writeAddressPartsToCluster(engine, cluster.fields, parts, onTouchKey)
          }
          renderManualEntry={() => (
            <AddressComposite
              legend={legend}
              entries={entries}
              footerToggle={footerToggle}
              prefillButton={prefillButton}
            />
          )}
        />
      );
    }
    if (unit.kind === 'height') {
      // Paired NumberInputs under one
      // legend. The catalog labels are "<prefix> height feet" /
      // "<prefix> height inches"; we project simplified labels ("ft"
      // / "in") so the row reads tightly.
      const feetField: FieldDefinition = {
        ...withUserHelpText(unit.feet),
        label: 'ft',
        helpText: '',
      };
      const inchesField: FieldDefinition = {
        ...withUserHelpText(unit.inches),
        label: 'in',
        helpText: '',
      };
      return (
        <fieldset
          key={unit.feet.id}
          className={styles.height_pair}
          data-height-pair={unit.feet.id}
        >
          <legend className={styles.height_pair__legend}>Height *</legend>
          <div className={styles.height_pair__grid}>
            <FieldRenderer
              field={feetField}
              engine={engine}
              touched={touchedFieldIds.has(unit.feet.id)}
              onTouch={() => onTouchKey(unit.feet.id)}
              uploadDocument={uploadDocument}
              onUploadError={onUploadError}
            />
            <FieldRenderer
              field={inchesField}
              engine={engine}
              touched={touchedFieldIds.has(unit.inches.id)}
              onTouch={() => onTouchKey(unit.inches.id)}
              uploadDocument={uploadDocument}
              onUploadError={onUploadError}
            />
          </div>
        </fieldset>
      );
    }
    if (unit.kind === 'name') {
      const { triplet } = unit;
      const issuesByName = new Map<string, readonly ValidationIssue[]>([
        [triplet.first.id, issuesByFieldId.get(triplet.first.id) ?? []],
        [triplet.middle.id, issuesByFieldId.get(triplet.middle.id) ?? []],
        [triplet.last.id, issuesByFieldId.get(triplet.last.id) ?? []],
      ]);
      // When the catalog labels follow the standard
      // "<prefix> first/middle/last name" pattern, project the
      // prefix into the fieldset legend and shorten the child
      // labels so we don't duplicate "Parent 1 legal" four times.
      // See projectNameTriplet for the full rationale.
      const projection = projectNameTriplet(
        triplet.first.label,
        triplet.middle.label,
        triplet.last.label,
      );
      const fallbackLegend =
        triplet.first.label.replace(/ first name.*/i, '').trim() ||
        'Legal name';
      const legend = projection?.legend ?? fallbackLegend;
      const projectChildLabel = (
        original: FieldDefinition,
        nextLabel: string | undefined,
      ): FieldDefinition => {
        const projected = withUserHelpText(original);
        if (nextLabel === undefined) return projected;
        return projected.label === nextLabel
          ? projected
          : { ...projected, label: nextLabel };
      };
      return (
        <NameComposite
          key={triplet.prefix}
          legend={legend}
          firstNameField={projectChildLabel(triplet.first, projection?.firstLabel)}
          middleNameField={projectChildLabel(triplet.middle, projection?.middleLabel)}
          lastNameField={projectChildLabel(triplet.last, projection?.lastLabel)}
          values={{
            first: readText(engine, triplet.first.id),
            middle: readText(engine, triplet.middle.id),
            last: readText(engine, triplet.last.id),
          }}
          issuesByFieldId={issuesByName}
          touchedFieldIds={touchedFieldIds}
          onChange={(sub, next) => {
            const f =
              sub === 'first'
                ? triplet.first
                : sub === 'middle'
                ? triplet.middle
                : triplet.last;
            const value: AnswerValue = { type: 'text', value: next };
            engine.setAnswer(f.id, value);
            onTouchKey(f.id);
          }}
          onBlur={sub => {
            const f =
              sub === 'first'
                ? triplet.first
                : sub === 'middle'
                ? triplet.middle
                : triplet.last;
            onTouchKey(f.id);
          }}
        />
      );
    }
    if (unit.kind === 'group') {
      const instanceIds =
        repeatableInstancesByGroup.get(unit.groupId) ?? [];
      // The catalog stores repeatableBound on every member of the
      // group; pull from the first member.
      const bound = unit.fields[0]?.repeatableBound;
      if (bound === undefined) {
        // Defensive — if a group's catalog entry omits the bound,
        // skip rendering rather than crash. Surface via the unit's
        // group id so devs notice in the console.
        // eslint-disable-next-line no-console
        console.warn(
          `[SectionRenderer] repeatable group ${unit.groupId} has no repeatableBound; skipping`,
        );
        return null;
      }
      return (
        <RepeatableGroupRenderer
          key={unit.groupId}
          legend={getRepeatableGroupLabel(unit.groupId)}
          groupId={unit.groupId}
          fields={unit.fields}
          instanceIds={instanceIds}
          bound={{ min: bound.min, max: bound.max }}
          onAddInstance={() => onAddInstance(unit.groupId)}
          onRemoveInstance={id => onRemoveInstance(unit.groupId, id)}
          engine={engine}
          touchedFieldIds={touchedFieldIds}
          onTouchKey={onTouchKey}
          uploadDocument={uploadDocument}
          onUploadError={onUploadError}
        />
      );
    }
    // Computed fields render as a read-only summary tile instead of
    // an editable input. The catalog declares the addend relationship;
    // `useComputedTotalSync` (above) keeps the engine value current.
    // The household-income field is a special case: it carries
    // threshold meaning USCIS cares about (Part 7 skip eligibility),
    // so the meter variant shows where the user lands relative to
    // 125% × FPG. Other computed tiles (asset totals) stay neutral.
    if (unit.field.computed !== undefined) {
      if ((unit.field.id as string) === 'sponsor.income.household_total_annual') {
        return (
          <HouseholdIncomeMeterTile
            key={unit.field.id}
            field={unit.field}
            state={incomeThreshold}
          />
        );
      }
      return (
        <ComputedTotalTile
          key={unit.field.id}
          field={unit.field}
          engine={engine}
        />
      );
    }
    // Singleton field.
    return (
      <FieldRenderer
        key={unit.field.id}
        field={unit.field}
        engine={engine}
        touched={touchedFieldIds.has(unit.field.id)}
        onTouch={() => onTouchKey(unit.field.id)}
        uploadDocument={uploadDocument}
        onUploadError={onUploadError}
      />
    );
  }
}
